/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClubManagers,
  inviteClubManager,
  postClubManagerResendInvite,
} from "../../api/clubApi";
import {
  canShowResendInvite,
  pickClubStaffUserId,
  pickInviteStatusKey,
  pickStaffEmail,
} from "../../helpers/clubStaffInvite";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import "./clubManagersAdmin.css";

/** MongoDB ObjectId string (24 hex chars). */
const CLUB_OBJECTID_HEX = /^[a-f0-9]{24}$/i;

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.managers)) return payload.managers;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.logs)) return payload.logs;
  return [];
};

const ClubManagersAdminContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const clubList = useSelector((s) => s?.club?.clubList ?? []);

  const initialClubId = searchParams.get("clubId") || "";
  const [selectedClubId, setSelectedClubId] = useState(initialClubId);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendingUserId, setResendingUserId] = useState(null);

  useEffect(() => {
    dispatch(clubListResponce());
  }, [dispatch]);

  useEffect(() => {
    if (initialClubId) setSelectedClubId(initialClubId);
  }, [initialClubId]);

  const clubOptions = useMemo(
    () =>
      (clubList || []).map((c) => ({
        id: c._id,
        label: c.name || c.slug || c._id,
      })),
    [clubList]
  );

  const loadManagers = useCallback(async () => {
    if (!selectedClubId) {
      setManagers([]);
      return;
    }
    setManagersLoading(true);
    try {
      const res = await fetchClubManagers(selectedClubId);
      const raw = res?.data ?? res;
      setManagers(asArray(raw));
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_MANAGERS_LOAD_ERROR"),
          type: "error",
        })
      );
      setManagers([]);
    } finally {
      setManagersLoading(false);
    }
  }, [selectedClubId, dispatch, t]);

  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  const onClubChange = (e) => {
    const id = e.target.value;
    setSelectedClubId(id);
    setSearchParams(id ? { clubId: id } : {});
  };

  const onInvite = async (e) => {
    e.preventDefault();
    const emailTrim = inviteEmail?.trim() || "";
    const nameTrim = inviteName?.trim() || "";
    if (!selectedClubId || !emailTrim) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_VALIDATION"),
          type: "error",
        })
      );
      return;
    }
    if (!CLUB_OBJECTID_HEX.test(String(selectedClubId).trim())) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_CLUBID_INVALID"),
          type: "error",
        })
      );
      return;
    }
    if (nameTrim.length < 1 || nameTrim.length > 200) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_NAME_INVALID"),
          type: "error",
        })
      );
      return;
    }
    setInviteBusy(true);
    try {
      await inviteClubManager({
        email: emailTrim,
        name: nameTrim,
        clubId: selectedClubId,
      });
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_SENT"),
          type: "success",
        })
      );
      setInviteEmail("");
      setInviteName("");
      loadManagers();
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_INVITE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setInviteBusy(false);
    }
  };

  const closeResendConfirm = () => {
    if (resendingUserId) return;
    setResendTarget(null);
  };

  const onConfirmResendInvite = async () => {
    const userId = pickClubStaffUserId(resendTarget);
    const email = pickStaffEmail(resendTarget);
    if (!userId || resendingUserId) return;
    setResendingUserId(userId);
    try {
      await postClubManagerResendInvite(userId);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_RESEND_OK", { email: email || "—" }),
          type: "success",
        })
      );
      setResendTarget(null);
      loadManagers();
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || t("CLUB_INVITE_RESEND_ERROR");
      dispatch(
        showToast({
          show: false,
          message,
          type: "error",
        })
      );
      if (
        typeof message === "string" &&
        message.toLowerCase().includes("already signed in")
      ) {
        setResendTarget(null);
        loadManagers();
      }
    } finally {
      setResendingUserId(null);
    }
  };

  const renderInviteStatusBadge = (row) => {
    const key = pickInviteStatusKey(row);
    if (key === "INVITED") {
      return (
        <Badge bg="warning" text="dark" className="fw-normal">
          {t("CLUB_INVITE_STATUS_PENDING")}
        </Badge>
      );
    }
    if (key === "ACTIVE") {
      return (
        <Badge bg="success" className="fw-normal">
          {t("CLUB_INVITE_STATUS_ACTIVE")}
        </Badge>
      );
    }
    return <span className="text-muted">—</span>;
  };

  const managerName = (m) => m.name ?? m.fullname ?? m.user?.fullname ?? "—";

  const renderManagerActions = (m) => {
    const userId = pickClubStaffUserId(m);
    const showResend = canShowResendInvite(m);
    const resendBusy = userId && resendingUserId === userId;
    if (!showResend) {
      return <span className="text-muted small">—</span>;
    }
    return (
      <Button
        variant="link"
        size="sm"
        className="p-0 text-decoration-none"
        disabled={Boolean(resendingUserId)}
        onClick={() => setResendTarget(m)}
      >
        {resendBusy ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            {t("LOADING")}
          </>
        ) : (
          t("CLUB_INVITE_RESEND")
        )}
      </Button>
    );
  };

  const pageTitle = [
    { title: t("EVENTS"), path: "/events-list" },
    { title: t("CLUB_MANAGEMENT"), path: "/clubs-management" },
    { title: t("CLUB_MANAGERS_PAGE_TITLE"), path: "#" },
  ];

  return (
    <div className="pageContent categoryManagement club-managers-admin">
      <Helmet>
        <title>{t("CLUB_MANAGERS_PAGE_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("CLUB_MANAGERS_PAGE_TITLE")}
        breadcrumbItem={t("CLUB_MANAGERS_PAGE_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />

      <Card className="mainCard mainCardWrapper club-managers-card">
        <Card.Body>
          <Form.Group className="mb-3 club-managers-club-select">
            <Form.Label>{t("CLUB_SELECT")}</Form.Label>
            <Form.Select value={selectedClubId} onChange={onClubChange}>
              <option value="">{t("CLUB_SELECT_PLACEHOLDER")}</option>
              {clubOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <h6 className="club-managers-section-title">{t("CLUB_INVITE_MANAGER")}</h6>
          <form
            onSubmit={onInvite}
            className="row g-3 align-items-end flex-wrap club-managers-invite-form"
          >
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Label>{t("EMAIL_ADDRESS")}</Form.Label>
              <Form.Control
                type="email"
                autoComplete="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                disabled={inviteBusy}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <Form.Label>{t("CLUB_MANAGER_NAME")}</Form.Label>
              <Form.Control
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder={t("CLUB_MANAGER_NAME_PH")}
                maxLength={200}
                required
                disabled={inviteBusy}
              />
            </div>
            <div className="col-12 col-lg-auto club-managers-invite-actions">
              <CustomButton
                type="submit"
                className="fillButton"
                loading={inviteBusy}
                disabled={inviteBusy || !selectedClubId}
              >
                {t("CLUB_INVITE_SEND")}
              </CustomButton>
            </div>
          </form>

          <h6 className="club-managers-section-title">{t("CLUB_MANAGERS_LIST")}</h6>
          {managersLoading ? (
            <p className="text-muted small mb-0">{t("LOADING")}</p>
          ) : managers.length === 0 ? (
            <p className="club-managers-empty">{t("NO_DATA_FOUND")}</p>
          ) : (
            <>
              <div className="club-managers-table-wrap">
                <Table bordered hover size="sm" className="club-managers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("CLUB_MANAGER_NAME")}</th>
                      <th>{t("EMAIL_ADDRESS")}</th>
                      <th>{t("CLUB_INVITE_STATUS")}</th>
                      <th>{t("CLUB_INVITE_ACTIONS")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((m, i) => (
                      <tr key={m._id || m.id || i}>
                        <td>{i + 1}</td>
                        <td>{managerName(m)}</td>
                        <td>{pickStaffEmail(m) || "—"}</td>
                        <td>{renderInviteStatusBadge(m)}</td>
                        <td>{renderManagerActions(m)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="club-managers-cards" aria-label={t("CLUB_MANAGERS_LIST")}>
                {managers.map((m, i) => (
                  <div key={m._id || m.id || `card-${i}`} className="club-managers-card-item">
                    <div className="club-managers-card-item-top">
                      <div>
                        <p className="club-managers-card-item-name">
                          {i + 1}. {managerName(m)}
                        </p>
                        <p className="club-managers-card-item-email">
                          {pickStaffEmail(m) || "—"}
                        </p>
                      </div>
                      {renderInviteStatusBadge(m)}
                    </div>
                    <div className="club-managers-card-item-actions">
                      {renderManagerActions(m)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="club-managers-refresh">
            <CustomButton
              type="button"
              className="borderButton btn-sm"
              onClick={loadManagers}
            >
              {t("REFRESH")}
            </CustomButton>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={resendTarget != null}
        onHide={closeResendConfirm}
        centered
        backdrop={resendingUserId ? "static" : true}
      >
        <Modal.Header closeButton={!resendingUserId}>
          <Modal.Title className="fs-6">
            {t("CLUB_INVITE_RESEND_CONFIRM_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {t("CLUB_INVITE_RESEND_CONFIRM_BODY", {
            email: pickStaffEmail(resendTarget) || "—",
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={closeResendConfirm}
            disabled={Boolean(resendingUserId)}
          >
            {t("CANCEL")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirmResendInvite}
            disabled={Boolean(resendingUserId)}
          >
            {resendingUserId ? t("LOADING") : t("CLUB_INVITE_RESEND")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClubManagersAdminContainer;
